import { useMemo } from 'react';

import { graphql } from '@/__generated__';
import {
  DataWarehouseEntityFilter,
  WarehouseEntitiesQuery,
} from '@/__generated__/graphql';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useLazyQuery } from '@/hooks/useLazyQuery';
import { useQueryStable } from '@/hooks/useQueryStable';
import { usePaginatedQuery } from '@/reducers/usePaginatedQuery';

export const GetWarehouseEntities = graphql(/* GraphQL */ `
  query WarehouseEntities(
    $accountId: BigInt!
    $filter: DataWarehouseEntityFilter
    $first: Int
    $after: String
  ) {
    catalog(accountId: $accountId) {
      dataPlatform {
        entities(filter: $filter, first: $first, after: $after) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              name
              type
              urn
              platform
              catalogPath {
                urn
                type
                name
              }
              ... on ConnectionNode {
                name
                type
                urn
                created
              }
              ... on DatabaseNode {
                name
                type
                urn
                externalUrl
                created
              }
              ... on SchemaNode {
                name
                type
                urn
                created
                externalUrl
              }
              ... on TableNode {
                name
                type
                urn
                created
                externalUrl
                rowCount
                sizeInBytes
              }
              ... on ViewNode {
                name
                type
                urn
                created
                externalUrl
              }
            }
          }
        }
      }
    }
  }
`);

export type WarehouseEntitiesParams = {
  first: number;
  after?: string;
  accountId: number | undefined;
  filter?: Partial<DataWarehouseEntityFilter>;
  pause?: boolean;
};

const getResults = (data: WarehouseEntitiesQuery) => {
  return data.catalog.dataPlatform.entities.edges.map((edge) => edge.node);
};

const getPageInfo = (data: WarehouseEntitiesQuery) => {
  const entities = data.catalog.dataPlatform.entities;
  return {
    totalCount: entities.totalCount ?? null,
    endCursor: entities.pageInfo.endCursor ?? null,
    hasNextPage: entities.pageInfo.hasNextPage,
  };
};

export const useWarehouseEntities = ({
  first,
  after,
  accountId,
  filter,
  pause,
}: WarehouseEntitiesParams) => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const shouldFetchWarehouseData =
    hasAccountFeature('catalogIngestion') && hasAccountFeature('explorerAccountUI');
  return usePaginatedQuery(
    {
      query: GetWarehouseEntities,
      variables: {
        first,
        after: after || '',
        accountId: accountId ?? 0,
        filter: {
          parent_urn: filter?.parent_urn ?? null,
          type: filter?.type ?? null,
          urn: filter?.urn ?? null,
          platform: filter?.platform ?? null,
          urns: filter?.urns ?? null,
        },
      },
      pause: pause || !accountId || !first || !shouldFetchWarehouseData,
    },
    getPageInfo,
    getResults,
    () => null,
  );
};

export const useLazyWarehouseEntities = () => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const shouldFetchWarehouseData =
    hasAccountFeature('catalogIngestion') && hasAccountFeature('explorerAccountUI');
  const [catalogEntities, queryExecute] = useLazyQuery(GetWarehouseEntities);

  const executeQuery = async (args: WarehouseEntitiesParams) => {
    if (!shouldFetchWarehouseData) {
      return {
        data: [],
        pageInfo: undefined,
        fetching: false,
        error: undefined,
        stale: false,
      };
    }
    const res = await queryExecute({
      first: args.first,
      after: args.after ?? '',
      accountId: args.accountId ?? 0,
      filter: {
        parent_urn: args.filter?.parent_urn ?? null,
        type: args.filter?.type ?? null,
        urn: args.filter?.urn ?? null,
        platform: args.filter?.platform ?? null,
        urns: args.filter?.urns ?? null,
      },
    });
    return {
      ...res,
      data: res.data ? getResults(res.data) : [],
      pageInfo: res.data ? getPageInfo(res.data) : undefined,
      fetching: false,
    };
  };

  return [
    {
      ...catalogEntities,
      data: catalogEntities.data ? getResults(catalogEntities.data) : [],
      pageInfo: catalogEntities.data ? getPageInfo(catalogEntities.data) : undefined,
    },
    executeQuery,
  ] as const;
};

export const GetWarehouseEntity = graphql(/* GraphQL */ `
  query WarehouseEntity($accountId: BigInt!, $filter: DataWarehouseEntityFilter) {
    catalog(accountId: $accountId) {
      dataPlatform {
        entities(filter: $filter, first: 1) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              accountId
              name
              type
              urn
              catalogPath {
                urn
                name
                type
              }
              platform
              ... on DatabaseNode {
                containerUrn
                database
                env
                created
                lastSyncedAt
                externalUrl
                removed
              }
              ... on SchemaNode {
                containerUrn
                database
                env
                created
                lastSyncedAt
                externalUrl
                removed
              }
              ... on TableNode {
                description
                containerUrn
                qualifiedName
                schemaName
                tags
                externalUrl
                removed
                created
                lastSyncedAt
                fields {
                  fieldPath
                  isPartOfKey
                  nativeDataType
                  nullable
                  recursive
                }
                rowCount
                columnCount
                sizeInBytes
              }
              ... on ViewNode {
                description
                containerUrn
                qualifiedName
                schemaName
                tags
                externalUrl
                removed
                created
                lastSyncedAt
                fields {
                  description
                  fieldPath
                  isPartOfKey
                  nativeDataType
                  nullable
                  recursive
                }
                viewLanguage
                viewLogic
              }
            }
          }
        }
      }
    }
  }
`);

export type WarehouseEntityParams = {
  accountId: number | undefined;
  filter?: Partial<DataWarehouseEntityFilter>;
  pause?: boolean;
};

export const useWarehouseEntity = ({
  accountId,
  filter,
  pause,
}: WarehouseEntityParams) => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const shouldFetchWarehouseData =
    hasAccountFeature('catalogIngestion') && hasAccountFeature('explorerAccountUI');
  const [{ fetching, error, data }] = useQueryStable({
    query: GetWarehouseEntity,
    variables: {
      accountId: accountId ?? 0,
      filter: {
        urn: filter?.urn ?? null,
        parent_urn: filter?.parent_urn ?? null,
        type: filter?.type ?? null,
        platform: filter?.platform ?? null,
        urns: filter?.urns ?? null,
      },
    },
    pause: pause || !accountId || !shouldFetchWarehouseData,
  });

  const entityQuery = useMemo(() => {
    const appliedEdge = data?.catalog.dataPlatform?.entities.edges?.[0];
    return {
      fetching,
      error,
      data: appliedEdge?.node,
    };
  }, [fetching, error, data]);
  return entityQuery;
};

export const useLazyWarehouseEntity = () => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const shouldFetchWarehouseData =
    hasAccountFeature('catalogIngestion') && hasAccountFeature('explorerAccountUI');
  const [catalogEntity, queryExecute] = useLazyQuery(GetWarehouseEntity);

  const executeQuery = async (args: WarehouseEntityParams) => {
    if (!shouldFetchWarehouseData) {
      return {
        data: undefined,
        fetching: false,
        error: undefined,
        stale: false,
      };
    }
    const res = await queryExecute({
      accountId: args.accountId ?? 0,
      filter: {
        parent_urn: args.filter?.parent_urn ?? null,
        type: args.filter?.type ?? null,
        urn: args.filter?.urn ?? null,
        platform: args.filter?.platform ?? null,
        urns: args.filter?.urns ?? null,
      },
    });
    return {
      ...res,
      data: res.data?.catalog.dataPlatform.entities.edges?.[0]?.node,
      fetching: false,
    };
  };

  return [
    {
      ...catalogEntity,
      data: catalogEntity.data?.catalog.dataPlatform.entities.edges?.[0]?.node,
    },
    executeQuery,
  ] as const;
};
